import { IRequestAction, progressEpic } from "epics/base-epic";
import _ from "lodash";
import { UNLINKED_MEDIA } from "constants/action-types";
import { ICoreUnlinkedMedia, ILinkUpdate, IUnlinkedMediaResource } from "constants/unlinked-media";
import siteConfig from "site-config";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import { add as snackbarAdd } from "actions/snackbars";
import { Observable } from "rxjs";
import { format } from "lib/globalize-util";
import { forkJoin } from "rxjs/observable/forkJoin";
import { mutateMediaAssets } from "../actions/product-media-actions";
import { IAsset } from "constants/assets";
import { IMediaAssetMap } from "../constants/media-asset";
import type { APIError } from "../lib/errors";

// prettier-ignore
export const fetchUnlinkedMediaEpic = progressEpic<IRequestAction, ICoreUnlinkedMedia[]>(
    UNLINKED_MEDIA.FETCH,
    ({ payload }) => ({ method: "GET", url: payload.url }),
    {
        dataCustomizer: (response) => ([...(response?.data ?? [])]),
    },
);

export const updateLinksStateEpic = (action$) =>
    action$
        .ofType(UNLINKED_MEDIA.UPDATE_LINKS_STATE.START)
        .map(({ payload }) => payload)
        .mergeMap(({ merchant_id, updates }) =>
            forkJoin(updateMediaLinks(merchant_id, updates))
                .map((responses) => {
                    return { responses };
                })
                .mergeMap(({ responses }) => [{ type: UNLINKED_MEDIA.UPDATE_LINKS_STATE.SUCCESS }])
                .catch(() => Observable.merge(Observable.of({ type: UNLINKED_MEDIA.UPDATE_LINKS_STATE.ERROR }))),
        );

const updateMediaLinks = (merchant_id: string, updates: ILinkUpdate[]) =>
    _.map(updates, (update) => updateMediaLink(merchant_id, update.id, update));

const updateMediaLink = (merchant_id: string, id: string, update: ILinkUpdate) =>
    ajax({
        method: "POST",
        url: `${siteConfig.goAPI}/v1/ingestionimage/${merchant_id}/${id}`,
        body: update,
    })
        .map(({ response }) => response)
        .catch(() => Observable.of(null));

export const linkMediaEpic = (action$) =>
    action$
        .ofType(UNLINKED_MEDIA.LINK.START)
        .map(({ payload }) => payload)
        .mergeMap(({ productId, revision, mediaMap, mediaResources, operations }) =>
            forkJoin(linkMediaResources(productId, mediaResources))
                .map((responses: Array<IAsset | null | APIError>) => {
                    let errors: Array<any>;
                    const populatedMediaMap = _.keys(mediaMap).reduce((result: IMediaAssetMap, key: string) => {
                        if (mediaMap[key].unlinkedMedia) {
                            const mediaAsset = responses.shift();
                            if (isApiError(mediaAsset)) {
                                // pass error info, if any, downstream the epic
                                (errors ?? (errors = [])).push(
                                    mediaAsset?.message ?? format("There was an error linking media to product"),
                                );
                            } else {
                                mediaMap[key].asset_id = mediaAsset?.asset_id;
                            }
                        }
                        result[key] = mediaMap[key];
                        return result;
                    }, {});
                    return { populatedMediaMap, errors };
                })
                .mergeMap(({ populatedMediaMap, errors }) => [
                    { type: errors ? UNLINKED_MEDIA.LINK.ERROR : UNLINKED_MEDIA.LINK.SUCCESS },
                    mutateMediaAssets(productId, revision, populatedMediaMap, operations, errors),
                ])
                .catch(() =>
                    Observable.merge(
                        Observable.of({ type: UNLINKED_MEDIA.LINK.ERROR }),
                        Observable.of(snackbarAdd({ message: format("There was an error linking media to product") })),
                    ),
                ),
        );

const linkMediaResources = (productId: string, mediaResources: IUnlinkedMediaResource[]) =>
    _.map(mediaResources, (mediaResource) => linkMediaResource(productId, mediaResource));

const linkMediaResource = (productId: string, mediaResource: IUnlinkedMediaResource) =>
    ajax({
        method: "POST",
        url: `${siteConfig.goAPI}/v1/coreproducts/${productId}/assets`,
        body: mediaResource,
    })
        .map(({ response }) => response)
        .catch((err) => Observable.of(_.isPlainObject(err.xhr.response) ? err.xhr.response : null));

const isApiError = (response: any): response is null | APIError => {
    return response === null || (_.isNumber(response?.code) && _.isString(response?.message));
};
